import { nextChangeAt, runningNow } from './schedule.js';
import { DEFAULT_THEME, type Background, type Material, type PassColours, type TabBarStyle, type ThemeColours } from './theme.js';
import { DEFAULT_TABS, type TabIcon, type TabId, type TabsSetting } from './tabs.js';
import { DEFAULT_PAGES, PAGE_IDS, enabledSections, type PageId, type PagesSetting } from './pages.js';
import { wordingFor, type WordingKey, type WordingOverrides } from './wording.js';
import type { ScheduledTheme } from './scheduled-themes.js';
import type { Theme } from './theme.js';
import { DEFAULT_CARD, type CardSetting } from './card.js';
import { DEFAULT_WELCOME, welcomeView, type WelcomeSetting, type WelcomeView } from './welcome.js';
import { DEFAULT_CARD_DESIGNS_SETTING, defaultDesign, type CardDesign, type CardDesignsSetting } from './designs.js';

/*
 * What GET /api/appearance sends (docs/26 §1), built in one place: the server answers with it, and the app uses
 * STARTING_APPEARANCE_VIEW until its first answer arrives (or when offline with nothing cached), so the starting look
 * is the same everywhere.
 */

/**
 * The shape's version: part of the server's ETag and the app's kept-copy key, so a phone holding an older answer (say,
 * the Card page's old section ids) fetches afresh instead of keeping it on a 304. Bump it when the shape changes.
 */
export const APPEARANCE_VIEW_VERSION = 9; // 9: the default card design rides along (`cardDesign`, 24 Oct 2026); 8: the Account page gained its 'creator' section (the Creator hub row, 22 Oct 2026); 7: it lost 'appearance' (light only)

export interface AppearanceView {
  /** Changes on every save; "default" while nothing has been saved. */
  version: string;
  theme: {
    /** "everyday", or the scheduled special's id. */
    id: string;
    name: string;
    light: ThemeColours;
    dark: ThemeColours;
    /** Relative URLs under /api/appearance/logos/, or null for the built-in logo. */
    logo: { light: string | null; dark: string | null };
    pass: PassColours;
    /** The tab bar's shape: "pill" (floating) or "standard" (on the bottom edge). */
    tabBar: TabBarStyle;
    /** The surfaces' material, the page background and whether anything moves (theme.ts). */
    material: Material;
    background: Background;
    motion: boolean;
  };
  /** When the look next changes by itself (a special starts or ends), or null. */
  nextChangeAt: string | null;
  tabs: { id: TabId; label: string; icon: TabIcon }[];
  accountButton: boolean;
  pages: Record<PageId, string[]>;
  wording: Record<WordingKey, string>;
  /** The Card page's QR style (CLAUDE.md §4 "Card"). */
  card: CardSetting;
  /** The first-open welcome (welcome.ts): the switch, the tagline and the cards with their photos as URLs. */
  welcome: WelcomeView;
  /** The default card design (designs.ts): the join page's card, and everyone the rules don't name. A customer's own comes with GET /api/me. */
  cardDesign: CardDesign;
}

export interface AppearanceParts { theme: Theme; themes: readonly ScheduledTheme[]; tabs: TabsSetting; pages: PagesSetting; wording: WordingOverrides; version: string; card?: CardSetting; welcome?: WelcomeSetting; cardDesigns?: CardDesignsSetting }

/** The theme in force now: the special running, or the everyday one. */
export function themeInForce(a: Pick<AppearanceParts, 'theme' | 'themes'>, now: Date): { id: string; name: string; theme: Theme } {
  const s = runningNow(a.themes, now);
  return s ? { id: s.id, name: s.name, theme: s.theme } : { id: 'everyday', name: 'Everyday', theme: a.theme };
}

export function appearanceView(a: AppearanceParts, now: Date, logoUrl: (key: string | null) => string | null, photoUrl: (key: string) => string = (k) => k): AppearanceView {
  const t = themeInForce(a, now);
  return {
    version: a.version,
    theme: { id: t.id, name: t.name, light: t.theme.light, dark: t.theme.dark, logo: { light: logoUrl(t.theme.logo.light), dark: logoUrl(t.theme.logo.dark) }, pass: t.theme.pass, tabBar: t.theme.tabBar, material: t.theme.material, background: t.theme.background, motion: t.theme.motion },
    nextChangeAt: nextChangeAt(a.themes, now),
    tabs: a.tabs.tabs.filter((x) => x.enabled).map(({ id, label, icon }) => ({ id, label, icon })),
    accountButton: a.tabs.accountButton,
    pages: Object.fromEntries(PAGE_IDS.map((p) => [p, enabledSections(a.pages, p)])) as Record<PageId, string[]>,
    wording: wordingFor(a.wording),
    card: a.card ?? DEFAULT_CARD,
    welcome: welcomeView(a.welcome ?? DEFAULT_WELCOME, photoUrl),
    cardDesign: defaultDesign(a.cardDesigns ?? DEFAULT_CARD_DESIGNS_SETTING),
  };
}

/** The starting look (Prototype D), in the shape the API sends. */
export const STARTING_APPEARANCE_VIEW: AppearanceView = appearanceView(
  { theme: DEFAULT_THEME, themes: [], tabs: DEFAULT_TABS, pages: DEFAULT_PAGES, wording: {}, version: 'default' },
  new Date(0),
  () => null,
);
